'use client';

import type { Card, CoreConfiguration } from '@adyen/adyen-web';
import { useLayoutEffect, useState } from 'react';
import { type AccountPaymentsSuccessResponse, logSentryError, SentryAcpCreditCardsErrors as SentryErrors } from '@libs/common-next';
import { getCountry } from '@libs/core';
import { adyenBaseConfiguration, adyenCardPlaceholders, adyenCardStyles } from '../../hooks/adyenConfig';
import type { CardState } from '../../hooks/types';
import { getAdyenBrand } from './cardBrand';

/**
 * Mounts an Adyen Card component for a stored card. Passing `storedPaymentMethodId` makes Adyen render
 * only the CVC input; expiry month/year are omitted on purpose so the read-only expiry field is not shown.
 * The backend resolves the real stored payment method from the card id, so the id here only switches
 * the component into stored-card mode.
 */
export function useStoredCardCvv(card: AccountPaymentsSuccessResponse | undefined, containerId: string) {
  const [component, setComponent] = useState<Card>();
  const [state, setState] = useState<CardState>();
  const cardId = card?.cardId;
  const cardType = card?.cardType;
  const lastFour = card?.lastFour;

  // useLayoutEffect so unmount runs while the container is still in the DOM and Adyen can
  // destroy its secured-field iframes before React removes the node.
  useLayoutEffect(() => {
    if (cardId === undefined || !cardType) return;
    const abortController = new AbortController();
    let mountedComponent: Card | undefined;
    const initializeAdyen = async () => {
      try {
        const {
          AdyenCheckout,
          Card
        } = await import('@adyen/adyen-web');
        const configuration: CoreConfiguration = {
          ...adyenBaseConfiguration,
          countryCode: getCountry(),
          onChange: cardState => setState(cardState as unknown as CardState),
          showPayButton: false
        };
        const checkout = await AdyenCheckout(configuration);
        if (abortController.signal.aborted) return;
        mountedComponent = new Card(checkout, {
          storedPaymentMethodId: String(cardId),
          brand: getAdyenBrand(cardType),
          lastFour,
          // Adyen only builds a stored card component when the stored method supports Ecommerce.
          // We don't load Adyen's payment methods response, so declare it here.
          supportedShopperInteractions: ['Ecommerce'],
          hideCVC: false,
          showContextualElement: false,
          placeholders: adyenCardPlaceholders,
          styles: adyenCardStyles,
          // Move focus into the CVV input as soon as it can take input.
          onConfigSuccess: () => mountedComponent?.setFocusOn('encryptedSecurityCode')
        }).mount(`#${containerId}`);
        setComponent(mountedComponent);
      } catch (error) {
        logSentryError(error, SentryErrors.CVVChallenge.InitializeAdyenFailed);
      }
    };
    initializeAdyen();
    return () => {
      abortController.abort();
      mountedComponent?.unmount();
      setComponent(undefined);
      setState(undefined);
    };
  }, [cardId, cardType, lastFour, containerId]);
  return {
    component,
    state
  };
}